/**
 * RenderWorkbench (Renders tab) — [REFACTOR-2026-06-06]
 *
 * The interactive polish workbench. A Render is created as a `draft` from a Mix
 * Plan (segment selection) and edited here: per-clip transition / speed /
 * caption / sound_effect / in-out, plus render-level BGM, opening voiceover
 * text, title cards / CTA, tail card and export profile. Edits are stored in
 * `render_params` (edit_spec) and auto-saved 1s after the last change. The
 * "导出" button runs FFmpeg to produce the final mp4; renders are re-exportable.
 *
 *   ┌────────┬─────────────────────────┬──────────────────────┐
 *   │ Render │ Player + timeline track │ Per-clip inspector   │
 *   │ list   │ + render-level controls │ + replace candidates │
 *   └────────┴─────────────────────────┴──────────────────────┘
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Download, Film, FolderUp, Loader2, Music, Save, X } from 'lucide-react'

import { dramaApi, dramaQueryKeys } from '@/lib/api/drama'
import type {
  DramaLang,
  DramaRender,
  DramaSegment,
  RenderClip,
  RenderEditSpec,
  TimelineSoundEffect,
  TimelineTransition,
} from '@/types/drama'
import type { Asset } from '@/types'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { toast } from '@/components/ui/toaster'

import { TimelinePlayer } from './TimelinePlayer'
import { TimelineTrack } from './TimelineTrack'
import { InOutSlider } from './InOutSlider'
import { SegmentReplacePanel } from './SegmentReplacePanel'
import { dramaT } from './i18n'
import { AssetPickerModal } from '@/components/library/AssetPickerModal'
import { BgmLibraryDialog } from './BgmLibraryDialog'

const TRANSITION_OPTIONS: TimelineTransition[] = ['hard_cut', 'dissolve', 'flash_white', 'flash_black']
// Most-common value in a list (used to reflect clips' predominant speed/transition).
function mode<T>(arr: T[]): T | undefined {
  if (!arr.length) return undefined
  const counts = new Map<T, number>()
  let best = arr[0]
  let bestN = 0
  for (const v of arr) {
    const n = (counts.get(v) ?? 0) + 1
    counts.set(v, n)
    if (n > bestN) { bestN = n; best = v }
  }
  return best
}

const DEFAULT_CAPTION_STYLE = {
  font_color: '#FFFFFF',
  font_size: 42,
  outline_color: '#000000',
  outline_width: 4,
  position: 'bottom' as const,
}
const SOUND_EFFECT_OPTIONS: TimelineSoundEffect[] = ['none', 'flashback']

const STATUS_VARIANT: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'outline'> = {
  draft: 'outline',
  pending: 'warning',
  running: 'warning',
  success: 'success',
  failed: 'destructive',
}

interface Props {
  projectId: string
  renders: DramaRender[]
  segments: DramaSegment[]
  lang: DramaLang
  activeRenderCode?: string | null
  onActiveRenderChange?: (code: string) => void
}

// [Part 3] Compact multi-select of clip orders, used by both the bulk-apply
// scope and the BGM per-clip range.
function ClipPicker({
  clips,
  selected,
  onChange,
}: {
  clips: RenderClip[]
  selected: number[]
  onChange: (orders: number[]) => void
}) {
  const set = new Set(selected)
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {clips.map((c) => {
        const on = set.has(c.order)
        return (
          <button
            key={c.order}
            type="button"
            onClick={() => {
              const next = new Set(set)
              on ? next.delete(c.order) : next.add(c.order)
              onChange(Array.from(next).sort((a, b) => a - b))
            }}
            className={`px-2 py-0.5 rounded text-[11px] border transition-colors ${
              on ? 'bg-foreground text-background' : 'bg-background hover:bg-muted'
            }`}
            title={c.segment_code}
          >
            #{c.order}
          </button>
        )
      })}
    </div>
  )
}

export function RenderWorkbench({
  projectId,
  renders,
  segments,
  lang,
  activeRenderCode,
  onActiveRenderChange,
}: Props) {
  const t = dramaT(lang)
  const [pickFor, setPickFor] = useState<'intro' | 'outro' | null>(null)
  const [bgmDialogOpen, setBgmDialogOpen] = useState(false)
  // [Part 3] Shared (render-level) param controls with apply-scope.
  const [bulkScope, setBulkScope] = useState<'whole' | 'clips'>('whole')
  const [bulkClipOrders, setBulkClipOrders] = useState<number[]>([])
  const [bulkSpeed, setBulkSpeed] = useState(1.0)
  const [bulkTransition, setBulkTransition] = useState<TimelineTransition>('hard_cut')
  const queryClient = useQueryClient()
  const segmentMap = useMemo(
    () => new Map(segments.map((s) => [s.segment_code, s])),
    [segments],
  )

  const [activeCode, setActiveCode] = useState<string | null>(
    activeRenderCode ?? renders[0]?.render_code ?? null,
  )
  useEffect(() => {
    if (activeRenderCode) setActiveCode(activeRenderCode)
  }, [activeRenderCode])

  const activeRender = renders.find((r) => r.render_code === activeCode) ?? null
  const [spec, setSpec] = useState<RenderEditSpec | null>(activeRender?.render_params ?? null)
  const [activeOrder, setActiveOrder] = useState<number | undefined>(undefined)
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Latest spec + dirty flag, kept in refs so the unload/unmount flush always
  // sends the most recent edit (no stale closure).
  const specRef = useRef<RenderEditSpec | null>(spec)
  const dirtyRef = useRef(false)
  const activeCodeRef = useRef<string | null>(activeCode)
  activeCodeRef.current = activeCode
  // Which render's params are currently loaded into `spec`. Lets us load the
  // edit_spec as soon as the render's data arrives (e.g. a freshly-created draft
  // that wasn't in `renders` yet when we switched to it) WITHOUT clobbering
  // unsaved edits on later refetches of the same render.
  const loadedCodeRef = useRef<string | null>(null)

  // Load edit_spec when the active render (for a new code) becomes available.
  useEffect(() => {
    if (activeRender && loadedCodeRef.current !== activeRender.render_code) {
      const ep = activeRender.render_params
      setSpec(ep)
      specRef.current = ep
      dirtyRef.current = false
      loadedCodeRef.current = activeRender.render_code
      setActiveOrder(ep.clips?.[0]?.order)
      // Reflect the clips' predominant speed / transition in the bulk controls so
      // the panel shows the current state after a refresh (these controls are an
      // applicator; the real values live per-clip).
      const cls = ep.clips ?? []
      if (cls.length) {
        setBulkSpeed(mode(cls.map((c) => c.playback_speed ?? 1)) ?? 1)
        setBulkTransition((mode(cls.map((c) => c.transition)) ?? 'hard_cut') as TimelineTransition)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRender])

  const saveMutation = useMutation({
    mutationFn: (s: RenderEditSpec) => {
      const code = activeCodeRef.current
      if (!code) throw new Error('no render selected')
      return dramaApi.patchRender(projectId, code, s)
    },
    onSuccess: () => {
      dirtyRef.current = false
      setSavedAt(new Date())
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })
    },
    onError: (e: any) =>
      toast({
        title: t('toast.saveFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      }),
  })

  const exportMutation = useMutation({
    mutationFn: async () => {
      const code = activeCodeRef.current
      if (!code) throw new Error('no render selected')
      // Flush any pending edits FIRST so the export renders the latest edit_spec
      // (otherwise it would use the last-saved server params).
      if (saveTimer.current) clearTimeout(saveTimer.current)
      if (specRef.current) {
        await dramaApi.patchRender(projectId, code, specRef.current)
        dirtyRef.current = false
      }
      return dramaApi.exportRender(projectId, code)
    },
    onSuccess: () => {
      // Export runs in the background now; the detail query polls render status
      // and refreshes the UI to success/failed automatically.
      toast({ title: t('toast.exportStarted') })
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })
    },
    onError: (e: any) =>
      toast({
        title: t('toast.exportFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      }),
  })

  // Auto-save shortly after each change. The exact spec is passed to the
  // mutation (not read from a closure) so the save can never send a stale value.
  const markDirty = (next: RenderEditSpec) => {
    setSpec(next)
    specRef.current = next
    dirtyRef.current = true
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => saveMutation.mutate(next), 600)
  }

  // [FIX-2026-06-08] Flush pending edits when leaving — on page unload (keepalive
  // fetch survives the refresh) and on unmount (component teardown). Without this
  // a refresh within the debounce window dropped the last edit.
  useEffect(() => {
    const flush = () => {
      if (!dirtyRef.current || !specRef.current || !activeCodeRef.current) return
      dramaApi.patchRenderKeepalive(projectId, activeCodeRef.current, specRef.current)
      dirtyRef.current = false
    }
    window.addEventListener('beforeunload', flush)
    return () => {
      window.removeEventListener('beforeunload', flush)
      if (saveTimer.current) clearTimeout(saveTimer.current)
      flush()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  // Resolve the selected BGM track's display name for the workbench label.
  const { data: bgmTracks = [] } = useQuery({
    queryKey: ['drama-bgm-library', projectId],
    queryFn: () => dramaApi.bgmLibrary(projectId),
  })

  const updateClip = (order: number, patch: Partial<RenderClip>) => {
    if (!spec) return
    markDirty({
      ...spec,
      clips: spec.clips.map((c) => (c.order === order ? { ...c, ...patch } : c)),
    })
  }

  const reorderClips = (fromIdx: number, toIdx: number) => {
    if (!spec) return
    const next = [...spec.clips]
    const [moved] = next.splice(fromIdx, 1)
    next.splice(toIdx, 0, moved)
    markDirty({ ...spec, clips: next.map((c, i) => ({ ...c, order: i + 1 })) })
  }

  const deleteClip = (order: number) => {
    if (!spec) return
    const next = spec.clips.filter((c) => c.order !== order).map((c, i) => ({ ...c, order: i + 1 }))
    markDirty({ ...spec, clips: next })
    if (activeOrder === order) setActiveOrder(next[0]?.order)
  }

  // [Part 3] Bulk-apply a shared param to the whole film or selected clips.
  // Per-clip Inspector tweaks below still override afterwards.
  const applyBulk = (field: 'playback_speed' | 'transition', value: number | TimelineTransition) => {
    if (!spec) return
    const set = new Set(bulkClipOrders)
    const targets = bulkScope === 'whole' ? spec.clips : spec.clips.filter((c) => set.has(c.order))
    if (!targets.length) {
      toast({ title: t('render.pickClips') })
      return
    }
    markDirty({
      ...spec,
      clips: spec.clips.map((c) =>
        bulkScope === 'whole' || set.has(c.order) ? { ...c, [field]: value } : c,
      ),
    })
    toast({ title: t('render.applied') })
  }

  const swapSegment = (newSegmentCode: string) => {
    if (!spec || !activeOrder) return
    const newSeg = segmentMap.get(newSegmentCode)
    if (!newSeg) return
    updateClip(activeOrder, {
      segment_code: newSegmentCode,
      episode_number: newSeg.episode_number,
      source_start_offset: 0,
      source_end_offset: null,
    })
  }

  if (renders.length === 0) {
    return (
      <div className="text-center py-12 border-2 border-dashed rounded-lg text-muted-foreground text-sm">
        {t('render.empty')}
      </div>
    )
  }

  if (!activeRender || !spec) return null

  const activeClip = spec.clips.find((c) => c.order === activeOrder)
  const activeSegment = activeClip ? segmentMap.get(activeClip.segment_code) : undefined
  const segDuration = activeSegment ? activeSegment.end_seconds - activeSegment.start_seconds : 0
  const outVal = activeClip?.source_end_offset != null ? activeClip.source_end_offset : segDuration
  const excludeCodes = spec.clips.map((c) => c.segment_code)
  const isExported = activeRender.status === 'success' && !!activeRender.output_url
  // Spinning while the background encode is actually running (status running/
  // pending), or during the brief export POST.
  const isExporting =
    exportMutation.isPending ||
    activeRender.status === 'running' ||
    activeRender.status === 'pending'
  // Selected BGM label: prefer the library track's name, fall back to the
  // legacy custom_name / mood for renders created before the library existed.
  const bgmLabel = spec.bgm.track_id
    ? bgmTracks.find((tr) => tr.id === spec.bgm.track_id)?.name || spec.bgm.custom_name || spec.bgm.track_id
    : spec.bgm.custom_name || (spec.bgm.mood ? t(`bgm.${spec.bgm.mood}` as any) : '')

  return (
    <div className="grid grid-cols-[240px_1fr_340px] gap-4">
      {/* LEFT: render list (all renders across all plans) */}
      <aside className="space-y-2">
        <h3 className="text-sm font-semibold mb-2">Renders ({renders.length})</h3>
        {renders.map((r) => (
          <button
            key={r.render_code}
            onClick={() => {
              setActiveCode(r.render_code)
              onActiveRenderChange?.(r.render_code)
            }}
            className={`w-full text-left p-2 rounded-md border ${
              r.render_code === activeCode
                ? 'bg-spectrum-magenta/10 border-spectrum-magenta'
                : 'hover:bg-accent/40'
            }`}
          >
            <div className="flex items-center justify-between">
              <code className="text-xs">{r.render_code}</code>
              <Badge variant={STATUS_VARIANT[r.status] ?? 'outline'}>{r.status}</Badge>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {t('render.plan', r.plan_code)}
              {r.duration_seconds != null && ` · ${r.duration_seconds.toFixed(1)}s`}
            </div>
          </button>
        ))}
      </aside>

      {/* MIDDLE: player + track + render-level controls */}
      <main className="space-y-4 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <code className="text-sm font-semibold">{activeRender.render_code}</code>
            <Badge variant="outline">{t('render.clips', spec.clips.length)}</Badge>
            <Badge variant={STATUS_VARIANT[activeRender.status] ?? 'outline'}>
              {activeRender.status}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {saveMutation.isPending ? (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> {t('render.saving')}
              </span>
            ) : savedAt ? (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-emerald-500" /> {t('render.savedAt')}{' '}
                {savedAt.toLocaleTimeString()}
              </span>
            ) : null}
            <Button size="sm" variant="outline" onClick={() => spec && saveMutation.mutate(spec)}>
              <Save className="w-3.5 h-3.5 mr-1" /> {t('render.save')}
            </Button>
            {/* [FIX-2026-06-09] Export runs in the BACKGROUND — keep the button
                spinning while the render is actually encoding (status running/
                pending), not just during the brief POST. detail polls the status. */}
            <Button
              size="sm"
              onClick={() => exportMutation.mutate()}
              disabled={isExporting}
            >
              {isExporting ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <Film className="w-3.5 h-3.5 mr-1" />
              )}
              {isExporting ? t('render.exporting') : t('render.export')}
            </Button>
          </div>
        </div>

        {/* Exported output preview + download */}
        {isExported && (
          <div className="p-3 border rounded-md bg-card space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{t('render.exported')}</span>
              <Button asChild variant="outline" size="sm">
                <a href={dramaApi.renderFileUrl(projectId, activeRender.render_code)} download={`${activeRender.render_code}.mp4`}>
                  <Download className="w-3.5 h-3.5 mr-1" /> {t('render.download')}
                </a>
              </Button>
            </div>
          </div>
        )}

        <TimelinePlayer
          projectId={projectId}
          clips={spec.clips}
          segments={segments}
          activeOrder={activeOrder}
          onActiveOrderChange={setActiveOrder}
          bgm={spec.bgm}
          captionStyle={spec.caption_style ?? DEFAULT_CAPTION_STYLE}
          intro={spec.intro}
          outro={spec.outro}
        />

        <TimelineTrack
          timeline={spec.clips}
          segmentMap={segmentMap}
          activeOrder={activeOrder}
          onSelect={setActiveOrder}
          onReorder={reorderClips}
          onDelete={deleteClip}
        />

        {/* [Part 3] Shared settings — apply speed / transition to whole film or
            selected clips (per-clip Inspector below still overrides). */}
        <div className="p-3 border rounded-md bg-card space-y-4">
          {/* Header + bulk apply-scope (inline, right-aligned) */}
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-medium">{t('render.sharedTitle')}</div>
              <div className="text-[10px] text-muted-foreground truncate">{t('render.sharedHint')}</div>
            </div>
            <div className="flex items-center gap-1 text-xs shrink-0">
              <span className="text-muted-foreground">{t('render.applyTo')}</span>
              {(['whole', 'clips'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setBulkScope(s)
                    if (s === 'clips') setBulkClipOrders(spec.clips.map((c) => c.order))
                  }}
                  className={`px-2 py-0.5 rounded border ${
                    bulkScope === s ? 'bg-foreground text-background' : 'bg-background hover:bg-muted'
                  }`}
                >
                  {s === 'whole' ? t('render.scopeWhole') : t('render.scopeClips')}
                </button>
              ))}
            </div>
          </div>
          {bulkScope === 'clips' && (
            <ClipPicker clips={spec.clips} selected={bulkClipOrders} onChange={setBulkClipOrders} />
          )}

          {/* bulk speed + transition — two compact columns with a divider */}
          <div className="grid grid-cols-2 border-t pt-4">
            <div className="flex items-center gap-1.5 pr-4">
              <Label className="text-xs shrink-0">{t('render.bulkSpeed')}</Label>
              <input
                type="range" min={0.5} max={1.5} step={0.05}
                value={bulkSpeed}
                onChange={(e) => setBulkSpeed(parseFloat(e.target.value))}
                className="flex-1 min-w-0 max-w-[120px]"
              />
              <span className="text-[11px] w-9 tabular-nums shrink-0">{bulkSpeed.toFixed(2)}x</span>
              <Button size="sm" variant="outline" className="h-7 px-2 shrink-0" onClick={() => applyBulk('playback_speed', bulkSpeed)}>
                {t('render.apply')}
              </Button>
            </div>
            <div className="flex items-center gap-1.5 pl-4 border-l">
              <Label className="text-xs shrink-0">{t('render.bulkTransition')}</Label>
              <select
                value={bulkTransition}
                onChange={(e) => setBulkTransition(e.target.value as TimelineTransition)}
                className="flex-1 min-w-0 max-w-[120px] px-1.5 py-1 border rounded-md bg-background text-xs"
              >
                {TRANSITION_OPTIONS.map((tr) => (
                  <option key={tr} value={tr}>{t(`trans.${tr}` as any)}</option>
                ))}
              </select>
              <Button size="sm" variant="outline" className="h-7 px-2 shrink-0" onClick={() => applyBulk('transition', bulkTransition)}>
                {t('render.apply')}
              </Button>
            </div>
          </div>

          {/* BGM — enable + pick on one row; volume + scope on the next */}
          <div className="space-y-2 border-t pt-4">
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs shrink-0">
                <input
                  type="checkbox"
                  checked={spec.bgm.enabled}
                  onChange={(e) => markDirty({ ...spec, bgm: { ...spec.bgm, enabled: e.target.checked } })}
                />
                {t('render.bgm')}
              </label>
              <Music className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="text-xs truncate flex-1 min-w-0">
                {bgmLabel || <span className="text-muted-foreground">{t('bgm.none')}</span>}
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 shrink-0"
                disabled={!spec.bgm.enabled}
                onClick={() => setBgmDialogOpen(true)}
              >
                {t('bgm.choose')}
              </Button>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Label className="text-xs whitespace-nowrap shrink-0">{t('render.volume', spec.bgm.volume.toFixed(2))}</Label>
              <input
                type="range" min={0} max={0.5} step={0.05}
                value={spec.bgm.volume}
                disabled={!spec.bgm.enabled}
                onChange={(e) => markDirty({ ...spec, bgm: { ...spec.bgm, volume: parseFloat(e.target.value) } })}
                className="w-24 shrink-0 disabled:opacity-50"
              />
              <span className="text-muted-foreground whitespace-nowrap shrink-0 ml-auto">{t('render.bgmScope')}</span>
              {(['whole', 'clips'] as const).map((s) => (
                <button
                  key={s}
                  disabled={!spec.bgm.enabled}
                  onClick={() =>
                    markDirty({
                      ...spec,
                      bgm: {
                        ...spec.bgm,
                        scope: s,
                        clip_orders: s === 'clips' ? spec.clips.map((c) => c.order) : (spec.bgm.clip_orders ?? []),
                      },
                    })
                  }
                  className={`px-2 py-0.5 rounded border shrink-0 disabled:opacity-50 ${
                    (spec.bgm.scope ?? 'whole') === s ? 'bg-foreground text-background' : 'bg-background hover:bg-muted'
                  }`}
                >
                  {s === 'whole' ? t('render.scopeWhole') : t('render.scopeClips')}
                </button>
              ))}
            </div>
            {spec.bgm.enabled && (spec.bgm.scope ?? 'whole') === 'clips' && (
              <ClipPicker
                clips={spec.clips}
                selected={spec.bgm.clip_orders ?? []}
                onChange={(orders) => markDirty({ ...spec, bgm: { ...spec.bgm, clip_orders: orders } })}
              />
            )}
          </div>

          <BgmLibraryDialog
            open={bgmDialogOpen}
            onOpenChange={setBgmDialogOpen}
            projectId={projectId}
            lang={lang}
            selectedTrackId={spec.bgm.track_id}
            onSelect={(track) => {
              markDirty({
                ...spec,
                bgm: {
                  ...spec.bgm,
                  enabled: true,
                  track_id: track.id,
                  // clear legacy fields so the new selection wins on export
                  mood: null,
                  custom_path: null,
                  custom_name: track.name,
                },
              })
              setBgmDialogOpen(false)
            }}
          />
        </div>

        {/* Render-level controls: intro/outro + caption style */}
        <div className="p-3 border rounded-md bg-card space-y-3">
          {/* 前贴 / 后贴 — imported from the Asset Library */}
          <div className="grid grid-cols-2 gap-3">
            {(['intro', 'outro'] as const).map((slot) => {
              const clip = spec[slot]
              return (
                <div key={slot}>
                  <Label className="text-xs">
                    {slot === 'intro' ? t('render.intro') : t('render.outro')}
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-muted-foreground truncate flex-1">
                      {clip ? (clip.name || clip.asset_id) : t('render.none')}
                    </span>
                    <Button size="sm" variant="outline" onClick={() => setPickFor(slot)}>
                      <FolderUp className="w-3.5 h-3.5 mr-1" />
                      {clip ? t('render.change') : t('render.importFromLibrary')}
                    </Button>
                    {clip && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive px-2"
                        onClick={() => markDirty({ ...spec, [slot]: null })}
                        title={t('render.remove')}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </main>

      {/* RIGHT: per-clip inspector + replace candidates */}
      <aside className="space-y-4">
        {activeClip ? (
          <div className="p-3 border rounded-md bg-card space-y-3">
            <div className="text-sm font-medium border-b pb-2">{t('render.inspectorTitle')}</div>
            <div className="flex items-center justify-between">
              <code className="text-xs">{activeClip.segment_code}</code>
              <Badge variant="outline">{t('render.order', activeClip.order)}</Badge>
            </div>
            <div className="text-[11px] text-muted-foreground">{t('render.purpose', activeClip.purpose)}</div>

            <div>
              <Label className="text-xs">{t('render.transition')}</Label>
              <select
                value={activeClip.transition}
                onChange={(e) => updateClip(activeClip.order, { transition: e.target.value as TimelineTransition })}
                className="w-full px-2 py-1.5 mt-1 border rounded-md bg-background text-sm"
              >
                {TRANSITION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{t(`trans.${opt}` as any)}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-xs">
                {t('render.speed', (activeClip.playback_speed ?? 1).toFixed(2))}
              </Label>
              <input
                type="range"
                min={0.5}
                max={1.5}
                step={0.1}
                value={activeClip.playback_speed ?? 1}
                onChange={(e) => updateClip(activeClip.order, { playback_speed: parseFloat(e.target.value) })}
                className="w-full mt-1"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {t('render.speedHint')}
              </p>
            </div>

            <div>
              <Label className="text-xs">{t('render.sfx')}</Label>
              <select
                value={activeClip.sound_effect || 'none'}
                onChange={(e) => updateClip(activeClip.order, { sound_effect: e.target.value as TimelineSoundEffect })}
                className="w-full px-2 py-1.5 mt-1 border rounded-md bg-background text-sm"
              >
                {SOUND_EFFECT_OPTIONS.map((s) => (
                  <option key={s} value={s}>{t(`sfx.${s}` as any)}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-xs mb-1.5 block">{t('render.inOut')}</Label>
              <InOutSlider
                segmentDuration={segDuration}
                inSeconds={activeClip.source_start_offset || 0}
                outSeconds={outVal}
                onChange={({ in: i, out }) => {
                  updateClip(activeClip.order, {
                    source_start_offset: i,
                    source_end_offset: out >= segDuration - 0.05 ? null : out,
                  })
                }}
              />
            </div>

            <div>
              <Label className="text-xs">{t('render.caption')}</Label>
              <Input
                value={activeClip.caption || ''}
                onChange={(e) => updateClip(activeClip.order, { caption: e.target.value || null })}
                placeholder={t('render.captionPlaceholder')}
                className="mt-1"
              />
              {/* 字幕样式 — collapsible; render-level (applies to all captions) */}
              {(() => {
                const cs = spec.caption_style ?? DEFAULT_CAPTION_STYLE
                const setCs = (patch: Partial<typeof cs>) =>
                  markDirty({ ...spec, caption_style: { ...cs, ...patch } })
                return (
                  <details className="mt-2 rounded border bg-background/60">
                    <summary className="cursor-pointer select-none px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground">
                      {t('render.captionStyle')}
                    </summary>
                    <div className="px-2 pb-2 space-y-2">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
                        <label className="flex items-center gap-1.5">
                          {t('render.capColor')}
                          <input type="color" value={cs.font_color}
                            onChange={(e) => setCs({ font_color: e.target.value })}
                            className="h-6 w-8 rounded border bg-background" />
                        </label>
                        <label className="flex items-center gap-1.5">
                          {t('render.capOutline')}
                          <input type="color" value={cs.outline_color}
                            onChange={(e) => setCs({ outline_color: e.target.value })}
                            className="h-6 w-8 rounded border bg-background" />
                        </label>
                        <label className="flex items-center gap-1.5">
                          {t('render.capPosition')}
                          <select value={cs.position}
                            onChange={(e) => setCs({ position: e.target.value as typeof cs.position })}
                            className="px-2 py-1 border rounded-md bg-background">
                            <option value="bottom">{t('render.capBottom')}</option>
                            <option value="center">{t('render.capCenter')}</option>
                            <option value="top">{t('render.capTop')}</option>
                          </select>
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs w-12 shrink-0">{t('render.capSize')}</Label>
                        <input type="range" min={12} max={72} step={1} value={cs.font_size}
                          onChange={(e) => setCs({ font_size: parseInt(e.target.value, 10) })}
                          className="flex-1" />
                        <span className="text-xs w-8 tabular-nums">{cs.font_size}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs w-12 shrink-0">{t('render.capOutlineW')}</Label>
                        <input type="range" min={0} max={8} step={1} value={cs.outline_width}
                          onChange={(e) => setCs({ outline_width: parseInt(e.target.value, 10) })}
                          className="flex-1" />
                        <span className="text-xs w-8 tabular-nums">{cs.outline_width}</span>
                      </div>
                      <div className="rounded bg-black/80 py-2 text-center">
                        <span style={{
                          color: cs.font_color,
                          fontSize: Math.max(11, cs.font_size / 3),
                          fontWeight: 700,
                          WebkitTextStroke: cs.outline_width ? `${Math.max(0.5, cs.outline_width / 3)}px ${cs.outline_color}` : undefined,
                        }}>
                          {t('render.capSample')}
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">{t('render.captionStyleHint')}</p>
                    </div>
                  </details>
                )
              })()}
            </div>
          </div>
        ) : (
          <div className="p-4 border rounded-md text-xs text-muted-foreground text-center">
            {t('render.selectClipHint')}
          </div>
        )}

        <SegmentReplacePanel
          projectId={projectId}
          currentItem={activeClip}
          excludeSegmentCodes={excludeCodes}
          allSegments={segments}
          lang={lang}
          onReplace={swapSegment}
        />
      </aside>

      {/* 前贴/后贴 importer — pick a video asset from the Library */}
      <AssetPickerModal
        open={pickFor !== null}
        assetType="video"
        title={pickFor === 'outro' ? t('render.pickOutro') : t('render.pickIntro')}
        onClose={() => setPickFor(null)}
        onSelect={(asset: Asset) => {
          if (pickFor && spec) {
            markDirty({ ...spec, [pickFor]: { asset_id: asset.id, name: asset.file_name || asset.id } })
          }
          setPickFor(null)
        }}
      />
    </div>
  )
}
